import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { patientApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Loader2, Printer, Layers } from 'lucide-react';
import { PrintPage, PRINT_CSS } from '../forms/PrintOriginalView';

function DocPicker({ records, selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="recert-doc-picker">
      {records.map(r => (
        <label key={r.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs cursor-pointer transition-colors ${selected.includes(r.id) ? 'border-[#0E7490] bg-[#F0FDFA] text-[#0A2342]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
          <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => onToggle(r.id)} data-testid={`recert-doc-${r.id}`} />
          {r.form_name} <span className="text-[#94A3B8]">· {new Date(r.last_updated_at).toLocaleDateString()}</span>
        </label>
      ))}
    </div>
  );
}

export default function RecertPacketPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState(null);
  const [selected, setSelected] = useState([]);
  const [docs, setDocs] = useState({});

  useEffect(() => {
    patientApi.get(patientId).then(r => setPatient(r.data)).catch(() => {});
    api.get(`/patients/${patientId}/form-records`).then(r => {
      const pdf = (r.data.records || []).filter(x => x.print_layout === 'pdf_original' && x.last_submission_id);
      setRecords(pdf);
      setSelected(pdf.map(x => x.id));
    }).catch(() => setRecords([]));
  }, [patientId]);

  useEffect(() => {
    (records || []).filter(r => selected.includes(r.id) && !docs[r.id]).forEach(r => {
      api.get(`/forms/${r.form_id}/print-original`, { params: { submission_id: r.last_submission_id } })
        .then(res => setDocs(d => ({ ...d, [r.id]: res.data })))
        .catch(() => setDocs(d => ({ ...d, [r.id]: { error: true } })));
    });
  }, [records, selected, docs]);

  const toggle = (id) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const ordered = (records || []).filter(r => selected.includes(r.id));
  const ready = ordered.every(r => docs[r.id]);

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="recert-packet-page">
      <div className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] p-3 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate(`/patients/${patientId}`)} data-testid="recert-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
          <div>
            <h1 className="text-sm font-semibold text-[#0A2342] flex items-center gap-1.5"><Layers className="w-4 h-4 text-[#0E7490]" />Recertification packet</h1>
            <div className="text-[11px] text-[#64748B]">{patient ? `${patient.first_name} ${patient.last_name}` : ''} · latest version of each form</div>
          </div>
        </div>
        <Button className="careiq-btn-primary" onClick={() => window.print()} disabled={!ordered.length || !ready} data-testid="recert-print-btn"><Printer className="w-4 h-4 mr-2" />Print packet</Button>
      </div>
      <div className="max-w-5xl mx-auto px-3 pt-4 print:hidden">
        {records === null ? <Loader2 className="w-6 h-6 animate-spin text-[#0E7490]" /> : records.length === 0
          ? <p className="text-sm text-[#94A3B8]" data-testid="recert-empty">No filled original-layout forms for this patient yet.</p>
          : <DocPicker records={records} selected={selected} onToggle={toggle} />}
      </div>
      <div className="max-w-5xl mx-auto py-6 space-y-6 print:py-0 print:space-y-0 print:max-w-none">
        {ordered.map(r => {
          const d = docs[r.id];
          if (!d) return <div key={r.id} className="flex justify-center py-8 print:hidden"><Loader2 className="w-6 h-6 animate-spin text-[#0E7490]" /></div>;
          if (d.error) return <p key={r.id} className="text-sm text-[#991B1B] print:hidden">Could not load {r.form_name}</p>;
          return (d.pages || []).map((pg, i) => (
            <PrintPage key={`${r.id}-${i}`} pageBase64={pg} fields={(d.fields || []).filter(f => (f.page_index ?? 0) === i)} />
          ));
        })}
      </div>
      <style>{PRINT_CSS}</style>
    </div>
  );
}
